import axios from 'axios';
import '../config.js';
import adminCatalogApi from './adminCatalogApi';

const TILE_TIME_LIMITATION_FEATURE = 'tile-time-limitation';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
  };
};

const base = (path) => `${global.config.url}${path}`;

const sortByName = (items) =>
  [...items].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

const normalizeLookupItems = (items, idKeyCandidates, nameKeyCandidates) => {
  if (!Array.isArray(items)) return [];

  const pick = (obj, candidates) => {
    for (const key of candidates) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
    }
    return undefined;
  };

  const normalized = items
    .map((item) => {
      const rawId = pick(item, idKeyCandidates);
      const rawName = pick(item, nameKeyCandidates);
      const id = Number(rawId);
      const name = String(rawName || '').trim();

      if (!Number.isFinite(id) || !name) return null;
      return { id, name };
    })
    .filter(Boolean);

  return sortByName(normalized);
};

export const getUsersSummary = async () => {
  const response = await axios.get(base('/auth/users/summary'), {
    headers: authHeaders(),
  });
  return Array.isArray(response.data) ? response.data : [];
};

export const getRoles = async () => {
  const response = await axios.get(base('/auth/roles'), {
    headers: authHeaders(),
  });
  return Array.isArray(response.data) ? response.data.map((x) => String(x)) : [];
};

export const getCustomersLookup = async () => {
  try {
    const response = await axios.get(base('/auth/customers/lookup'), {
      headers: authHeaders(),
    });

    return normalizeLookupItems(response.data, ['id', 'customerId'], ['name', 'customerName']);
  } catch (error) {
    const response = await axios.get(base('/support/getcustomers?appProviderId=0'), {
      headers: authHeaders(),
    });
    return normalizeLookupItems(response.data, ['id', 'customerId'], ['name', 'customerName']);
  }
};

export const getLocationsByCustomerLookup = async (customerId) => {
  const response = await axios.get(base(`/locations/ByCustomer/${customerId}`), {
    headers: authHeaders(),
  });

  return normalizeLookupItems(response.data, ['id', 'locationId'], ['name', 'locationName']);
};

export const getAppProvidersLookup = async () => {
  const appProviders = await adminCatalogApi.getAppProviders();
  return appProviders.map((item) => ({
    id: Number(item.id),
    name: `${String(item.appName || '').trim()} - ${String(item.company || '').trim()}`,
  }));
};

export const createUser = async (payload) => {
  const response = await axios.post(base('/auth/register'), payload, {
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json;charset=UTF-8',
    },
  });
  return response.data;
};

export const getUserById = async (userId) => {
  const response = await axios.get(base(`/auth/users/${userId}`), {
    headers: authHeaders(),
  });
  return response.data;
};

export const getUserFeatureFlags = async (userId) => {
  const user = await getUserById(userId);
  return Array.isArray(user && user.featureFlags)
    ? user.featureFlags.map((featureName) => String(featureName || '').trim()).filter(Boolean)
    : [];
};

export const setNotificationSettingsTester = async (userId, enabled) => {
  const response = await axios.post(
    base(`/auth/users/${userId}/notification-settings-tester?enabled=${Boolean(enabled)}`),
    {},
    { headers: authHeaders() }
  );
  return response.data;
};

export const setUserFeatureFlag = async (userId, featureName, enabled) => {
  const safeFeature = encodeURIComponent(String(featureName || '').trim());
  const response = await axios.post(
    base(`/auth/users/${userId}/feature-flags/${safeFeature}?enabled=${Boolean(enabled)}`),
    {},
    { headers: authHeaders() }
  );
  return response.data;
};

/**
 * Set or clear a feature flag for all users under a role.
 * @param {string} roleName
 * @param {string} featureName
 * @param {boolean} enabled
 * @returns {Promise<void>}
 */
export const setRoleFeatureFlag = async (roleName, featureName, enabled) => {
  const safeRoleName = encodeURIComponent(String(roleName || '').trim());
  const safeFeatureName = encodeURIComponent(String(featureName || '').trim());

  await axios.post(
    base(`/auth/roles/${safeRoleName}/feature-flags/${safeFeatureName}?enabled=${Boolean(enabled)}`),
    {},
    { headers: authHeaders() }
  );
};

export const setTileTimeLimitationFeatureFlag = async (userId, enabled) => {
  return setUserFeatureFlag(userId, TILE_TIME_LIMITATION_FEATURE, enabled);
};

export const updateUser = async (userId, payload) => {
  const response = await axios.put(base(`/auth/users/${userId}`), payload, {
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json;charset=UTF-8',
    },
  });
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await axios.delete(base(`/auth/users/${userId}`), {
    headers: authHeaders(),
  });
  return response.data;
};

export const getFeatures = async () => {
  const response = await axios.get(base('/auth/features'), {
    headers: authHeaders(),
  });
  return response.data && typeof response.data === 'object' ? response.data : {};
};

export const setGlobalFeature = async (featureName, enabled) => {
  const safeFeature = encodeURIComponent(String(featureName || '').trim());
  const response = await axios.post(
    base(`/auth/features/${safeFeature}`),
    { enabled: Boolean(enabled) },
    {
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json;charset=UTF-8',
      },
    }
  );
  return response.data;
};

const adminApi = {
  getUsersSummary,
  getRoles,
  getCustomersLookup,
  getLocationsByCustomerLookup,
  getAppProvidersLookup,
  createUser,
  getUserById,
  getUserFeatureFlags,
  setNotificationSettingsTester,
  setUserFeatureFlag,
  setRoleFeatureFlag,
  setTileTimeLimitationFeatureFlag,
  updateUser,
  deleteUser,
  getFeatures,
  setGlobalFeature,
};

export default adminApi;
